import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider,
    FormControl, FormHelperText, FormLabel, Input, Option, Select, Sheet,
    Stack, Typography,
} from '@mui/joy';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

import { useAdvertiser } from '../../../AdvertiserContext';
import {
    APIAdsAdjustmentCreate, APIAdsAdjustmentDelete,
    APIAdsAdjustmentUpdate, APIAdsAdjustments, adsErrorText,
} from '../../../api_ads';
import { useAdsDictionaries, useAdsPlacements } from '../useAdsData';
import {
    ADJUSTMENT_DIMENSION_LABELS, AGE_BUCKETS, WEEKDAYS,
    formatPercentDelta, multiplierToPercent, percentToMultiplier,
} from '../adsConstants';


const MIN_PERCENT = -90;
const MAX_PERCENT = 900;


/**
 * Корректировки ставок по сегментам.
 *
 * Вводятся процентами: рекламодатель мыслит «женщинам +50%», а не
 * «множитель 1.50». На сервер уходит множитель, диапазон −90%…+900%
 * ровно соответствует допустимым 0.10–10.00.
 */
export default function AdBidAdjustments({ campaign, editable = true }) {
    const { advertiserToken: token } = useAdvertiser();
    const { dictionaries } = useAdsDictionaries();
    const { placements } = useAdsPlacements();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);

    const [dimension, setDimension] = useState('gender');
    const [key, setKey] = useState('');
    const [percent, setPercent] = useState(20);
    const [scheduleDay, setScheduleDay] = useState('0');
    const [scheduleHour, setScheduleHour] = useState('*');

    const load = useCallback(async () => {
        if (!token || !campaign?.id) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdsAdjustments(token, campaign.id);
            setItems(res?.items || []);
        } catch (e) {
            setError(adsErrorText(e, 'Не удалось загрузить корректировки'));
        } finally {
            setLoading(false);
        }
    }, [token, campaign?.id]);

    useEffect(() => { load(); }, [load]);

    // Варианты значений зависят от выбранного измерения
    const keyOptions = useMemo(() => {
        if (dimension === 'gender') {
            return (dictionaries?.genders || []).map((g) => ({
                value: String(g.value), label: g.label,
            }));
        }
        if (dimension === 'age') {
            return AGE_BUCKETS.map((b) => ({ value: b, label: b }));
        }
        if (dimension === 'platform') {
            return (dictionaries?.platforms || []).map((p) => ({
                value: p.value, label: p.label,
            }));
        }
        if (dimension === 'paid_status') {
            return [
                { value: 'paid', label: 'С активным тарифом' },
                { value: 'free', label: 'Без тарифа' },
            ];
        }
        if (dimension === 'placement') {
            return (campaign?.campaign_placements || []).map((link) => ({
                value: link.placement_code, label: link.placement_name,
            }));
        }
        return null; // гео и расписание вводятся своими полями
    }, [dimension, dictionaries, campaign]);

    useEffect(() => {
        setKey(keyOptions?.[0]?.value ?? '');
    }, [dimension, keyOptions]);

    /**
     * Максимально возможное произведение множителей: по одному
     * наибольшему из каждого измерения. Сервер зажимает итог в 0.1–10,
     * и без предупреждения часть корректировок молча не сработает.
     */
    const maxProduct = useMemo(() => {
        const byDimension = {};
        items.filter((i) => i.is_enabled).forEach((item) => {
            const value = Number(item.multiplier);
            byDimension[item.dimension] = Math.max(byDimension[item.dimension] || 0, value);
        });
        return Object.values(byDimension).reduce((acc, value) => acc * value, 1);
    }, [items]);

    const grouped = useMemo(() => {
        const map = {};
        items.forEach((item) => {
            (map[item.dimension] = map[item.dimension] || []).push(item);
        });
        return map;
    }, [items]);

    const resolveKey = () => {
        if (dimension === 'schedule') return `${scheduleDay}-${scheduleHour}`;
        return String(key || '').trim();
    };

    const add = async () => {
        const finalKey = resolveKey();
        if (!finalKey) { setError('Укажите значение для корректировки'); return; }
        const numeric = Number(percent);
        if (!Number.isFinite(numeric) || numeric < MIN_PERCENT || numeric > MAX_PERCENT) {
            setError(`Корректировка должна быть от ${MIN_PERCENT}% до +${MAX_PERCENT}%`);
            return;
        }
        try {
            setBusy('add');
            setError(null);
            await APIAdsAdjustmentCreate(token, campaign.id, {
                dimension,
                key: finalKey,
                multiplier: percentToMultiplier(numeric),
                is_enabled: true,
            });
            await load();
        } catch (e) {
            setError(adsErrorText(e));
        } finally {
            setBusy(null);
        }
    };

    const updatePercent = async (item, newPercent) => {
        const numeric = Number(newPercent);
        if (!Number.isFinite(numeric) || numeric < MIN_PERCENT || numeric > MAX_PERCENT) return;
        try {
            setBusy(`edit-${item.id}`);
            await APIAdsAdjustmentUpdate(token, item.id, {
                multiplier: percentToMultiplier(numeric),
            });
            await load();
        } catch (e) {
            setError(adsErrorText(e));
        } finally {
            setBusy(null);
        }
    };

    const toggle = async (item) => {
        try {
            setBusy(`toggle-${item.id}`);
            await APIAdsAdjustmentUpdate(token, item.id, { is_enabled: !item.is_enabled });
            await load();
        } catch (e) {
            setError(adsErrorText(e));
        } finally {
            setBusy(null);
        }
    };

    const remove = async (item) => {
        try {
            setBusy(`del-${item.id}`);
            await APIAdsAdjustmentDelete(token, item.id);
            await load();
        } catch (e) {
            setError(adsErrorText(e));
        } finally {
            setBusy(null);
        }
    };

    const keyLabel = (item) => {
        if (item.dimension === 'gender') {
            return (dictionaries?.genders || [])
                .find((g) => String(g.value) === String(item.key))?.label || item.key;
        }
        if (item.dimension === 'platform') {
            return (dictionaries?.platforms || [])
                .find((p) => p.value === item.key)?.label || item.key;
        }
        if (item.dimension === 'paid_status') {
            return item.key === 'paid' ? 'С активным тарифом' : 'Без тарифа';
        }
        if (item.dimension === 'placement') {
            return placements.find((p) => p.code === item.key)?.name || item.key;
        }
        if (item.dimension === 'schedule') {
            const [day, hour] = String(item.key).split('-');
            const dayLabel = WEEKDAYS.find((d) => d.value === day)?.label || day;
            return hour === '*' ? `${dayLabel}, весь день` : `${dayLabel}, ${hour}:00`;
        }
        return item.key;
    };

    if (loading) {
        return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size="sm" /></Stack>;
    }

    return (
        <Box>
            <Typography level="title-sm" sx={{ mb: 0.5 }}>Корректировки ставок</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Повышают или понижают ставку для отдельных сегментов аудитории.
                Корректировки из разных измерений перемножаются.
            </Typography>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            {maxProduct > 10 && (
                <Alert color="warning" sx={{ mb: 2 }}>
                    <Box>
                        <Typography level="title-sm">
                            Корректировки могут перемножиться в ×{maxProduct.toFixed(1)}
                        </Typography>
                        <Typography level="body-sm">
                            Итоговый множитель ограничен десятикратным, поэтому для части
                            аудитории корректировки сработают не в полную силу. Уменьшите
                            значения, чтобы результат был предсказуемым.
                        </Typography>
                    </Box>
                </Alert>
            )}

            {items.length === 0 ? (
                <Sheet variant="soft" sx={{ p: 3, borderRadius: 'md', textAlign: 'center', mb: 2 }}>
                    <Typography level="body-sm" color="neutral">
                        Корректировок нет — ставка одинакова для всей аудитории
                    </Typography>
                </Sheet>
            ) : (
                <Stack spacing={2} sx={{ mb: 2 }}>
                    {Object.entries(grouped).map(([dim, rows]) => (
                        <Sheet key={dim} variant="outlined" sx={{ p: 1.5, borderRadius: 'md' }}>
                            <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                                {ADJUSTMENT_DIMENSION_LABELS[dim] || dim}
                            </Typography>
                            <Stack spacing={1}>
                                {rows.map((item) => (
                                    <Stack
                                        key={item.id}
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{ opacity: item.is_enabled ? 1 : 0.5 }}
                                    >
                                        <Typography level="body-sm" sx={{ flex: 1, minWidth: 0 }}>
                                            {keyLabel(item)}
                                        </Typography>

                                        <Input
                                            size="sm"
                                            type="number"
                                            defaultValue={multiplierToPercent(item.multiplier)}
                                            disabled={!editable || busy === `edit-${item.id}`}
                                            onBlur={(e) => {
                                                const next = Number(e.target.value);
                                                if (next !== multiplierToPercent(item.multiplier)) {
                                                    updatePercent(item, next);
                                                }
                                            }}
                                            endDecorator="%"
                                            sx={{ width: 110 }}
                                        />

                                        <Chip
                                            size="sm"
                                            variant="soft"
                                            color={Number(item.multiplier) >= 1 ? 'success' : 'warning'}
                                        >
                                            ×{Number(item.multiplier).toFixed(2)}
                                        </Chip>

                                        <Button
                                            size="sm"
                                            variant="plain"
                                            color="neutral"
                                            disabled={!editable}
                                            loading={busy === `toggle-${item.id}`}
                                            onClick={() => toggle(item)}
                                        >
                                            {item.is_enabled ? 'Выключить' : 'Включить'}
                                        </Button>

                                        <Button
                                            size="sm"
                                            variant="plain"
                                            color="danger"
                                            disabled={!editable}
                                            loading={busy === `del-${item.id}`}
                                            onClick={() => remove(item)}
                                        >
                                            <DeleteOutlineRoundedIcon fontSize="small" />
                                        </Button>
                                    </Stack>
                                ))}
                            </Stack>
                        </Sheet>
                    ))}
                </Stack>
            )}

            {editable && (
                <>
                    <Divider sx={{ mb: 2 }} />
                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'md' }}>
                        <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>
                            Добавить корректировку
                        </Typography>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1.2}
                            alignItems={{ xs: 'stretch', md: 'flex-end' }}
                        >
                            <FormControl size="sm" sx={{ minWidth: 170 }}>
                                <FormLabel>Измерение</FormLabel>
                                <Select value={dimension} onChange={(_, v) => v && setDimension(v)}>
                                    {Object.entries(ADJUSTMENT_DIMENSION_LABELS).map(([value, label]) => (
                                        <Option key={value} value={value}>{label}</Option>
                                    ))}
                                </Select>
                            </FormControl>

                            {keyOptions ? (
                                <FormControl size="sm" sx={{ flex: 1, minWidth: 180 }}>
                                    <FormLabel>Значение</FormLabel>
                                    <Select value={key} onChange={(_, v) => setKey(v ?? '')}>
                                        {keyOptions.map((option) => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : dimension === 'schedule' ? (
                                <>
                                    <FormControl size="sm" sx={{ minWidth: 150 }}>
                                        <FormLabel>День</FormLabel>
                                        <Select
                                            value={scheduleDay}
                                            onChange={(_, v) => v && setScheduleDay(v)}
                                        >
                                            {WEEKDAYS.map((d) => (
                                                <Option key={d.value} value={d.value}>{d.label}</Option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="sm" sx={{ minWidth: 130 }}>
                                        <FormLabel>Час</FormLabel>
                                        <Select
                                            value={scheduleHour}
                                            onChange={(_, v) => v && setScheduleHour(v)}
                                        >
                                            <Option value="*">Весь день</Option>
                                            {Array.from({ length: 24 }, (_, h) => (
                                                <Option key={h} value={String(h)}>{h}:00</Option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </>
                            ) : (
                                <FormControl size="sm" sx={{ flex: 1, minWidth: 200 }}>
                                    <FormLabel>
                                        {dimension === 'geo_city' ? 'Город' : 'Регион'}
                                    </FormLabel>
                                    <Autocomplete
                                        options={(dimension === 'geo_city'
                                            ? dictionaries?.cities
                                            : dictionaries?.regions) || []}
                                        value={key || null}
                                        onChange={(_, v) => setKey(v || '')}
                                        placeholder="Начните вводить"
                                    />
                                </FormControl>
                            )}

                            <FormControl size="sm" sx={{ width: 140 }}>
                                <FormLabel>Корректировка</FormLabel>
                                <Input
                                    type="number"
                                    value={percent}
                                    onChange={(e) => setPercent(e.target.value)}
                                    endDecorator="%"
                                />
                                <FormHelperText>от −90 до +900</FormHelperText>
                            </FormControl>

                            <Button
                                size="sm"
                                startDecorator={<AddRoundedIcon />}
                                loading={busy === 'add'}
                                onClick={add}
                            >
                                Добавить
                            </Button>
                        </Stack>

                        {Number(percent) !== 0 && (
                            <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                                Ставка для этого сегмента станет{' '}
                                <b>{formatPercentDelta(percent)}</b> —
                                множитель ×{percentToMultiplier(percent)}
                            </Typography>
                        )}
                    </Sheet>
                </>
            )}
        </Box>
    );
}